/**
 * v3 Aurora notification row statuses. Mirror of
 * `notifications/v3/common-utils/src/types/enum.ts` `notificationStatusEnum`.
 *
 * @readonly
 * @enum {string}
 */
export const NotificationStatus = Object.freeze({
    PENDING: "PENDING",
    SENT: "SENT",
    ERROR: "ERROR",
    CANCELED: "CANCELED",
    DELETED: "DELETED",
    /**
     * CORRESPONDENCE only: file landed in S3 but the HRP status update failed.
     */
    CORRESPONDENCE_HRP_UPDATE_PENDING: "CORRESPONDENCE_HRP_UPDATE_PENDING",
    /** Terminal status for CORRESPONDENCE when HRP could not be updated after all retries. */
    CORRESPONDENCE_HRP_UPDATE_ERROR: "CORRESPONDENCE_HRP_UPDATE_ERROR",
});

/**
 * Statuses included in the outbox Errors queue segment (`GET /notifications?status=errors`).
 *
 * @readonly
 * @type {readonly string[]}
 */
export const NOTIFICATION_QUEUE_ERROR_STATUSES = Object.freeze([
    NotificationStatus.ERROR,
    NotificationStatus.CORRESPONDENCE_HRP_UPDATE_ERROR,
]);

/**
 * Statuses that behave like pending for display (pill styling, scheduled timestamp).
 *
 * @readonly
 * @type {readonly string[]}
 */
export const NOTIFICATION_PENDING_LIKE_STATUSES = Object.freeze([
    NotificationStatus.PENDING,
    NotificationStatus.CORRESPONDENCE_HRP_UPDATE_PENDING,
]);

/**
 * @param {unknown} status
 * @returns {string}
 */
export function normalizeNotificationStatus(status) {
    return String(status ?? "").trim().toUpperCase();
}

/**
 * Bulk/single queue actions apply only to exact {@link NotificationStatus.PENDING}
 * (not {@link NotificationStatus.CORRESPONDENCE_HRP_UPDATE_PENDING}).
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isActionablePendingNotificationStatus(status) {
    return (
        normalizeNotificationStatus(status) === NotificationStatus.PENDING
    );
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isPendingLikeNotificationStatus(status) {
    return NOTIFICATION_PENDING_LIKE_STATUSES.includes(
        normalizeNotificationStatus(status),
    );
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isErrorLikeNotificationStatus(status) {
    return NOTIFICATION_QUEUE_ERROR_STATUSES.includes(
        normalizeNotificationStatus(status),
    );
}

/**
 * Picks `sentTimestamp` or `scheduledTimestamp` to display based on notification status.
 *
 * @param {{ sentTimestamp?: string | null, scheduledTimestamp?: string, status?: unknown } | null | undefined} notification
 * @param {unknown} [statusOverride]
 * @returns {string | null | undefined}
 */
export function getNotificationDisplayTimestamp(
    notification,
    statusOverride,
) {
    if (notification == null) {
        return undefined;
    }

    const status = normalizeNotificationStatus(
        statusOverride ?? notification.status,
    );
    const sent = notification.sentTimestamp;
    const scheduled = notification.scheduledTimestamp;

    if (status === NotificationStatus.SENT) {
        return sent;
    }
    if (isPendingLikeNotificationStatus(status)) {
        return scheduled;
    }
    if (sent != null && sent !== "") {
        return sent;
    }
    return scheduled;
}
